import React, { useState } from 'react';
import { X, Check, Star, Calendar } from 'lucide-react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { SUBSCRIPTION_PLANS } from '../../const/subscriptionPlan';
import { CREDIT_TOPUP_PACKS } from '../../const/creditTopUpPacks';
import REPORT_TYPES_DISPLAY from '../../const/reportType';
import FormattedDateInput from '../Forms/FormattedDateInput';
const PlanFeature = ({ children, className = "" }) => (
  <li className={`flex items-start gap-2 text-sm text-gray-800 mb-2 ${className}`}>
    <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

const ReportModal = ({ isOpen, onClose, onPurchase }) => {
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const [selectedAddons, setSelectedAddons] = useState({
    essentialSingleTransit: false,
    completeThreeMonthTransit: false,
  });

  const [dates, setDates] = useState({
    essentialTransitDate: '',
    singleTransitDate: '',
    threeMonthStartDate: '',
    completeThreeMonthStartDate: '',
  });

  if (!isOpen) return null;

  const handlePurchase = (planType, price) => {
    // Validation for date-specific plans
    if (planType === 'single_date_transit' && !dates.singleTransitDate) {
      alert('Please select a date for the Single Date Transit report.');
      return;
    }
    if (planType === 'three_month_transit' && !dates.threeMonthStartDate) {
      alert('Please select a start date for the 3-Month Transit report.');
      return;
    }
    onPurchase(planType, {
      price,
      addons: selectedAddons,
      dates: dates,
    });
  };

  const handleDateChange = (key, value) => {
    setDates(prev => ({ ...prev, [key]: value }));
  };

  const getReportButtonLabel = (paidLabel) => {
    if (user?.planTier !== SUBSCRIPTION_PLANS.PREMIUM) {
      return paidLabel;
    }
    return 'Generate Report (Included)';
  };

  const handleReportAction = (planType, price) => {
    handlePurchase(planType, price);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative animate-in zoom-in-95 duration-200 flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
        >
          <X size={24} />
        </button>

        <div className="p-8 overflow-y-auto custom-scrollbar" style={{ backgroundImage: "url('/BGForDialog.webp')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">Unlock Your Cosmic Blueprint</h2>
            <p className="text-gray-200">Choose the depth of insight that calls to you</p>
            {user?.planTier === SUBSCRIPTION_PLANS.PREMIUM && (
              <div className="mt-4 flex justify-center gap-4">
                <div className="bg-green-100 text-green-800 px-4 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                  <Star size={14} className="fill-green-800" />
                  {user.credits || 0} Credits Remaining
                </div>
                <div className="bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                  <Calendar size={14} />
                  Expires: {user.subscriptionEndDate ? new Date(user.subscriptionEndDate).toLocaleDateString() : '-'}
                </div>
              </div>
            )}
          </div>
            {/* Instruction Section */}
            <div className="max-w-2xl mx-auto mb-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5 text-center">
              <p className="text-white text-xxl md:text-lg font-medium">
                Have a subscription?
              </p>
              <p className="text-gray-200 text-sm md:text-base mb-3">
              Unlock charts with credits for full interpretations. Reports for unlocked charts are included at no extra cost.
              </p>

              <div className="h-px bg-white/20 my-3 w-16 mx-auto"></div>

              <p className="text-white text-xxl md:text-lg font-medium">
                Prefer a single report?
              </p>
              <p className="text-gray-200 text-sm md:text-base">
              Pick your Report, pay securely and we'll email it to you.
              </p>
              <p className="text-white text-xxl md:text-lg font-medium">
                *Reports can also be found on your Profile page for download. 
              </p>
            </div>
          {/* One-Time Reports Section */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Star className="w-6 h-6 text-purple-400 fill-purple-400" />
              One-Time Reports
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">

              {/* Essential Natal */}
              <div className="border border-white/60 bg-white backdrop-blur-md rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition-all duration-300 relative group flex flex-col">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Essential Natal</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$39.99</span>
                  </div>
                </div>
                <ul className="mb-6 flex-grow">
                  <PlanFeature><strong>Major Planets (Planetary Positions)</strong></PlanFeature>
                  <PlanFeature><strong>Complete Aspect Section</strong></PlanFeature>
                  <PlanFeature><strong>Chart Interpretation</strong></PlanFeature> 
                </ul>
                <button
                  onClick={() => handleReportAction(REPORT_TYPES_DISPLAY.essential_natal, 39.99)}
                  className="w-full py-2.5 px-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  {getReportButtonLabel('Buy Essential Natal Report')}
                </button>
              </div>

              {/* Complete Soul */}
              <div className="border-2 border-purple-500 rounded-xl p-6 shadow-xl relative bg-white backdrop-blur-md flex flex-col overflow-hidden">
                <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  MOST POPULAR
                </div>
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Complete Soul</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$69.99</span>
                  </div>
                </div>
                <ul className="mb-6 flex-grow">
                  <PlanFeature><strong>All Planetary Positions</strong></PlanFeature>
                  <PlanFeature><strong>Dwarf Planets</strong></PlanFeature>
                  <PlanFeature><strong>Complete Aspect Section</strong></PlanFeature>
                  <PlanFeature><strong>Overall Chart Interpretation</strong></PlanFeature>
                </ul>
                <button
                  onClick={() => handleReportAction(REPORT_TYPES_DISPLAY.complete_soul, 69.99)}
                  className="w-full py-2.5 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                  {getReportButtonLabel('Buy Complete Soul Report')}
                </button>
              </div>

              {/* Ultimate */}
              <div className="border border-white/60 bg-white backdrop-blur-md rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition-all duration-300 flex flex-col">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Ultimate</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$89.99</span>
                  </div>
                </div>
                <ul className="mb-6 flex-grow">
                  <PlanFeature><strong>All Planetary Positions</strong></PlanFeature>
                  <PlanFeature><strong>Dwarf Planets</strong></PlanFeature>
                  <PlanFeature><strong>Complete Aspect Section</strong></PlanFeature>
                  <PlanFeature><strong>Overall Chart Interpretation</strong></PlanFeature>
                  <PlanFeature><strong>Transit Analysis Included</strong></PlanFeature>
                  <PlanFeature><strong>3-Month Transit Forecast(From current date)</strong></PlanFeature>
                </ul>
                <button
                  onClick={() => handleReportAction(REPORT_TYPES_DISPLAY.ultimate, 89.99)}
                  className="w-full py-2.5 px-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors mt-auto"
                >
                  {getReportButtonLabel('Buy Ultimate Report')}
                </button>
              </div>

              {/* Single Date Transit */}
              <div className="border border-white/60 bg-white backdrop-blur-md rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition-all duration-300 flex flex-col">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Single Date Transit</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$19.99</span>
                  </div>
                </div>
                <div className="mb-6 flex-grow">
                  <p className="text-sm text-gray-700 mb-4">Report for a specific date.</p>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Select Date</label>
                  <FormattedDateInput
                    name="singleTransitDate"
                    value={dates.singleTransitDate}
                    onChange={(e) => handleDateChange('singleTransitDate', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <button
                  onClick={() => handleReportAction(REPORT_TYPES_DISPLAY.single_date_transit, 19.99)}
                  className="w-full py-2.5 px-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  {getReportButtonLabel('Buy Single Date Transit')}
                </button>
              </div>

              {/* 3-Month Transit */}
              <div className="border border-white/60 bg-white backdrop-blur-md rounded-xl p-6 hover:border-purple-500 hover:shadow-lg transition-all duration-300 flex flex-col">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">3-Month Transit</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$39.99</span>
                  </div>
                </div>
                <div className="mb-6 flex-grow">
                  <p className="text-sm text-gray-700 mb-4">Report from selected date to next 3 months.</p>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Select Start Date</label>
                  <FormattedDateInput
                    name="threeMonthStartDate"
                    value={dates.threeMonthStartDate}
                    onChange={(e) => handleDateChange('threeMonthStartDate', e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <button
                  onClick={() => handleReportAction(REPORT_TYPES_DISPLAY.three_month_transit, 39.99)}
                  className="w-full py-2.5 px-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  {getReportButtonLabel('Buy 3-Month Transit')}
                </button>
              </div>

            </div>
          </div>

          {/* Subscriptions Section */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-indigo-400" />
              Subscriptions
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">

              {/* Free Plan */}
              <div className="border border-white/60 bg-white backdrop-blur-sm rounded-xl p-6 hover:border-indigo-500 transition-all duration-300 flex flex-col">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Free</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$0</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                </div>
                <ul className="mb-6 flex-grow space-y-2">
                  <PlanFeature>Generate chart one time</PlanFeature>
                  <PlanFeature>Interpretations: 1 planet/week</PlanFeature>
                  <PlanFeature>Partial Overall Chart Interpretation</PlanFeature>
                  <li className="flex items-start gap-2 text-sm text-gray-500 mb-2">
                    <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Transit (Locked)</span>
                  </li>
                </ul>
                <button
                  disabled
                  className="w-full py-2.5 px-4 bg-gray-200 text-gray-500 rounded-lg font-semibold cursor-not-allowed"
                >
                  {user?.planTier === SUBSCRIPTION_PLANS.PREMIUM ? "Included" : "Current Plan"}
                </button>
              </div>

              {/* Premium Plan */}
              <div className="border-2 border-indigo-500 rounded-xl p-6 shadow-lg relative bg-white backdrop-blur-md flex flex-col">
                <div className="mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Premium Plan</h4>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">$49.99</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                  {user?.planTier === SUBSCRIPTION_PLANS.PREMIUM && (
                    <div className="mt-4 p-2 bg-indigo-100 rounded-lg">
                      <p className="text-indigo-800 font-semibold">
                        Your Credits: {user?.credits || 0}
                      </p>
                      {user?.subscriptionEndDate && (
                        <p className="text-xs text-indigo-600 mt-1">
                          Expires in{" "}
                          {Math.max(
                            0,
                            Math.ceil(
                              (new Date(user.subscriptionEndDate) - new Date()) /
                              (1000 * 60 * 60 * 24)
                            )
                          )}{" "}
                          days
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <ul className="mb-6 flex-grow space-y-2">
                  <PlanFeature><strong>Full Access</strong> to Interpretations</PlanFeature>
                  <PlanFeature><strong>30 Credits</strong> per month (1 credit = 1 Interpretation)</PlanFeature>
                  <PlanFeature>Reports included for unlocked charts</PlanFeature>
                  <PlanFeature>Full Access to all planets</PlanFeature>
                  <PlanFeature>Reports usage visible in Profile</PlanFeature>
                </ul>
                {hasActiveSubscription ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 text-center mb-2">
                      Top-up credits expire when your subscription ends.
                    </p>
                    {CREDIT_TOPUP_PACKS.map((pack) => (
                      <button
                        key={pack.planType}
                        type="button"
                        onClick={() => handlePurchase(pack.planType, pack.price)}
                        className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex justify-between items-center"
                      >
                        <span>{pack.label}</span>
                        <span>${pack.price.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={() => handlePurchase('premium', 49.99)}
                    className="w-full py-2.5 px-4 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    {user?.planTier === SUBSCRIPTION_PLANS.PREMIUM ? "Renew Subscription" : "Upgrade to Premium"}
                  </button>
                )}
              </div>

            </div>
          </div>

        </div>
      </div>
    </div >
  );
};

ReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPurchase: PropTypes.func.isRequired,
};

export default ReportModal;
